import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { DiscountsApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi, getApiErrorMessage } from "@/lib/utils";
import type { useToast } from "@/components/feedback";

export const useDiscountDelete = (
  toast: ReturnType<typeof useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (discountId: string) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(DiscountsApi, accessToken);

      return service.apiDiscountsDiscountIdDelete({
        discountId,
      });
    },
    onSuccess: async (_, discountId) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Discounts],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Discount, discountId],
      });
      toast({
        title: "Discount deleted",
        description: "Discount deleted successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error: unknown) => {
      const errorMessage = await getApiErrorMessage(
        error,
        "This discount is still assigned to seasons or classes and cannot be deleted. Remove it from those records first.",
      );

      toast({
        title: "Cannot delete discount",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};